import { useCrsfToken } from "~/hooks/useCrsfToken"
import type { CategoryType } from "../types"
import type { fetchItemsResponseType, CreateOrderOptions } from "./type"

const BASE_URL = '/api/store'

async function fetchCategories(): Promise<CategoryType[]> {
    const url = `${BASE_URL}/categories`
    const response = await fetch(url)
    const result = await response.json()
    return result.data
}

type fetchItemsRequestType = {
    category_id: number
    page?: number
    order_by?: string
    search?: string
    attributes?: number[]
    signal: AbortSignal
}

async function fetchItems({ category_id, page, order_by, search, attributes, signal }: fetchItemsRequestType): Promise<fetchItemsResponseType> {
    const url = `${BASE_URL}/listings`
    const params = new URLSearchParams()
    if (category_id) params.append('category_id', category_id.toString())
    if (page) params.append('page', page.toString())
    if (order_by) params.append('order_by', order_by)
    if (search) params.append('search', search)
    if (attributes && attributes.length > 0) {
        params.append('attributes', JSON.stringify(attributes))
    }
    const response = await fetch(`${url}?${params}`, { signal })
    const result = await response.json()
    return result.data
}

export const PURCHASE_FAILED = 'The store is not available right now for this account.'

/**
 * `fetch` rejects on a dropped connection and on nothing else, so a 400 arrives here looking
 * exactly like a sale. Without this check the caller `await`ed a refusal, took it for an order and
 * showed the shopper "Thank you for your purchase!" -- for a gate refusal, an insufficient balance,
 * an unavailable listing, every one of them.
 *
 * The message comes from the server rather than from here: `refusal_reason` composes it from
 * whichever gate refused, and a sentence written next to the rule is worth more than one guessed
 * at in the client. `PURCHASE_FAILED` covers a body that carries nothing usable.
 */
async function createOrder(options: CreateOrderOptions) {
    const url = `${BASE_URL}/orders/nft/create`
    const csrfToken = useCrsfToken()
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(options),
    })

    let body: any = null
    try {
        body = await response.json()
    } catch {
        // A gate that answers with HTML rather than JSON, or an empty body.
    }

    if (!response.ok) {
        throw new Error(body?.errors?.__all__?.[0] || body?.message || PURCHASE_FAILED)
    }
    return body
}

export const StoreApi = {
    fetchCategories,
    fetchItems,
    createOrder,
}
